import { AMQPWebSocketClient } from "@cloudamqp/amqp-client"
import { useCallback, useEffect, useRef, useState, useMemo } from "react"
import {
  REACT_APP_CLOUD_AMQP_URL,
  REACT_APP_CLOUD_AMQP_USERNAME,
  REACT_APP_CLOUD_AMQP_PASSWORD,
  REACT_APP_USE_LOCAL_AMQP_AUTH,
} from "src/global/env"
import { useOrganizations } from "src/pages/OrganizationRoot/hooks/useOrganizations"
import { MessageBrokerContext } from "src/global/messageBroker/context"
import { Action, EventActionMap, Message, AMQPConnection, AMQPChannel } from "src/global/messageBroker/types"
import { camelCaseKeys } from "src/global/utils/camelCase"
import { useCurrentUser } from "src/global/hooks/useCurrentUser"
import { backOff, IBackOffOptions } from "exponential-backoff"
import { JitterType } from "exponential-backoff/dist/options"
import { ConnectionLostAlert } from "src/global/messageBroker/ConnectionLostAlert"

export const INITIAL_RECONNECT_DELAY = 1000
export const MAX_RECONNECT_DELAY = 30000
export const BACKOFF_FACTOR = 2
export const MAX_RECONNECT_ATTEMPTS = 10
export const BACKOFF_JITTER: JitterType = "full"

// Configure backoff strategy
const BACKOFF_OPTIONS: IBackOffOptions = {
  numOfAttempts: MAX_RECONNECT_ATTEMPTS,
  startingDelay: INITIAL_RECONNECT_DELAY,
  timeMultiple: BACKOFF_FACTOR,
  maxDelay: MAX_RECONNECT_DELAY,
  delayFirstAttempt: false,
  jitter: BACKOFF_JITTER,
  retry: (error: Error, attemptNumber: number) => {
    console.info(`[AMQP] Connection retry (attempt ${attemptNumber}/${MAX_RECONNECT_ATTEMPTS}):`, error)
    return true
  },
}

export async function setupAMQPConnection(getAccessTokenSilently: () => Promise<string>, userId?: string) {
  const options = {
    heartbeat: 30,
    vhost: "/",
  }

  if (REACT_APP_USE_LOCAL_AMQP_AUTH) {
    return new AMQPWebSocketClient(
      REACT_APP_CLOUD_AMQP_URL,
      options.vhost,
      REACT_APP_CLOUD_AMQP_USERNAME,
      REACT_APP_CLOUD_AMQP_PASSWORD,
      undefined, // name
      undefined, // frameMax
      options.heartbeat,
    )
  }

  const accessToken = await getAccessTokenSilently()
  return new AMQPWebSocketClient(
    REACT_APP_CLOUD_AMQP_URL,
    options.vhost,
    userId,
    accessToken,
    undefined, // name
    undefined, // frameMax
    options.heartbeat,
  )
}

interface MessageBrokerProviderProps {
  children: React.ReactNode
}

export function MessageBrokerProvider({ children }: MessageBrokerProviderProps) {
  const { currentOrg } = useOrganizations()
  const { getAccessTokenSilently, user } = useCurrentUser()

  const orgId = currentOrg?.id
  const shouldEstablishConnection = Boolean(orgId)

  const [eventActions] = useState<EventActionMap>(new Map())
  const [isConnectionLost, setConnectionLost] = useState(false)
  const [liveUpdatesUnavailable, setLiveUpdatesUnavailable] = useState(false)
  const connectionRef = useRef<AMQPConnection | null>(null)
  const channelRef = useRef<AMQPChannel | null>(null)
  const isAmqpInitializedRef = useRef(false)

  const registerActionForEvent = (event: string, action: Action) => {
    if (!shouldEstablishConnection) return

    const actions = eventActions.get(event) || []
    if (!actions.some((existingAction: Action) => existingAction.toString() === action.toString())) {
      eventActions.set(event, [...actions, action])
    }
  }

  const unregisterActionForEvent = (event: string, action: Action) => {
    if (!shouldEstablishConnection) return

    const actions = eventActions.get(event)
    if (actions) {
      const filteredActions = actions.filter(
        (existingAction: Action) => existingAction.toString() !== action.toString(),
      )
      if (filteredActions.length === 0) {
        eventActions.delete(event)
      } else {
        eventActions.set(event, filteredActions)
      }
    }
  }

  const setupAndSubscribe = useCallback(async () => {
    const amqp = await setupAMQPConnection(getAccessTokenSilently, user?.id)

    amqp.onerror = (error) => {
      console.error("[AMQP] Connection error:", error)
      setConnectionLost(true)
    }

    const connection = await amqp.connect()
    connectionRef.current = connection
    console.info("[AMQP] Connection established successfully")

    const channel = await connection.channel()
    channelRef.current = channel

    // Create a unique, temporary queue for this connection
    const queue = await channel.queue("", {
      autoDelete: true, // Delete when connection closes
    })

    // orgId exists here because shouldEstablishConnection is true
    // TypeScript still needs us to handle the theoretical null case
    const exchange = `client-${orgId ?? ""}`

    // Declare exchange if it doesn't exist
    await channel.exchangeDeclare(exchange, "fanout", { durable: true })
    await queue.bind(exchange)

    await queue.subscribe({ noAck: false }, (msg) => {
      const content = msg.bodyToString()
      if (content) {
        try {
          const msgJson = JSON.parse(content)
          const message: Message = camelCaseKeys(msgJson)
          const actions = eventActions.get(message.event)
          actions?.forEach((registeredAction: Action) => {
            registeredAction(message)
          })
        } catch (err) {
          // Silent fail - we don't want to break the UI for message parsing errors
          console.error("[AMQP] Error processing message:", err)
        }
      }
      msg.ack()
    })

    isAmqpInitializedRef.current = true
    setConnectionLost(false)

    return connection
  }, [getAccessTokenSilently, user?.id, orgId, eventActions])

  const start = useCallback(async () => {
    try {
      await backOff(async () => {
        return await setupAndSubscribe()
      }, BACKOFF_OPTIONS)
    } catch (err) {
      console.error(`[AMQP] Failed to reconnect after ${MAX_RECONNECT_ATTEMPTS} attempts. Giving up.`, err)
      setLiveUpdatesUnavailable(true)
    }
  }, [setupAndSubscribe])

  const shouldSkipConnection = useMemo(() => {
    // Skip if real-time updates are disabled and we're not attempting to reconnect
    const isDisabledAndNotReconnecting = !shouldEstablishConnection && !isConnectionLost

    // Skip if we're already initialized and not attempting to reconnect
    const isInitializedAndNotReconnecting = isAmqpInitializedRef.current && !isConnectionLost

    return isDisabledAndNotReconnecting || isInitializedAndNotReconnecting
  }, [shouldEstablishConnection, isConnectionLost])

  useEffect(() => {
    if (shouldSkipConnection) return

    void start()

    return () => {
      void (async () => {
        try {
          if (channelRef.current) {
            await channelRef.current.close()
          }
          if (connectionRef.current) {
            await connectionRef.current.close()
          }
          isAmqpInitializedRef.current = false
        } catch (err) {
          // Silent fail - cleanup errors shouldn't affect the UI
          console.error("[AMQP] Error during cleanup:", err)
        }
      })()
    }
  }, [
    eventActions,
    shouldEstablishConnection,
    orgId,
    getAccessTokenSilently,
    start,
    isConnectionLost,
    shouldSkipConnection,
  ])

  const value = {
    registerActionForEvent,
    unregisterActionForEvent,
  }

  return (
    <MessageBrokerContext.Provider value={value}>
      {liveUpdatesUnavailable && <ConnectionLostAlert setConnectionLost={setLiveUpdatesUnavailable} />}
      {children}
    </MessageBrokerContext.Provider>
  )
}
